/**
 * Contextual Training Scorer
 * 
 * Pure scoring function for ranking training artifacts based on ticket context.
 * Used by TicketLearningPanel and Learning Center recommendations.
 * 
 * Scoring Logic:
 * 1. FUEL TYPE GATE (hard filter) - must pass to be considered
 * 2. KEYWORD MATCHING (additive score)
 *    - Title match: +10 points per keyword
 *    - Notes match: +5 points per keyword
 *    - DTC match: +15 points per code
 * 3. CONFIDENCE BOOST (multiplier)
 *    - verified: 1.5x
 *    - community_validated: 1.2x
 *    - provisional: 1.0x
 * 4. JOB MAPPING BOOST (if exists)
 *    - Has training_artifact_job_map entry: +20 points
 *    - relevance_weight applied as multiplier
 * 5. DURATION SCORING (mode-based)
 *    - Job Specific: Hard filter >30 min, boost short content
 *    - Foundational: Boost long-form and live/in-person
 */

import type { TrainingArtifact, SidebarMode } from '@/types/training';
// Batch 18-D6: confidence-tier multipliers extracted into a shared
// scoring module. Values are unchanged. The call site below applies
// them multiplicatively, exactly as before.
import { CONFIDENCE_MULTIPLIERS } from './training/scoring/confidenceTier';
// Batch 18-D12: the private generateExplanation helper was moved into
// a shared explanations module and renamed `generateContextualExplanation`
// for cross-scorer namespace clarity. The helper body is text-identical
// to the prior local version; the call site in scoreArtifactsForTicket
// uses the new identifier.
// Batch 18-D13: dropped the vestigial `durationBoosted` parameter from
// the helper signature. The scorer's call site and the
// `calculateDurationScore` destructuring above are updated in lockstep.
import { generateContextualExplanation } from './training/explanations/contextualExplanation';
// Batch 18-D14: the private getConfidenceBadge helper was moved into a
// shared scoring/display module. The badge values ('verified' /
// 'community' / 'provisional') are unchanged; the call site in
// scoreArtifactsForTicket continues to assign the result to
// ScoredContextualArtifact.confidenceBadge unchanged.
import { getConfidenceBadge } from './training/scoring/confidenceBadge';
// Batch 18-D17: the private calculateAdjacencyBoost helper was moved
// into a shared contextual scoring module and renamed
// `calculateContextualAdjacencyBoost` for cross-scorer namespace
// clarity (the engine scorer does not use job adjacency). Helper body
// text-identical to the prior local version.
// Batch 18-D18: trimmed the previously-vestigial `matchedJobId` return
// field. The helper now returns a plain `number`; the call site below
// uses a direct assignment instead of a destructure. Numeric output for
// every input path is unchanged.
import { calculateContextualAdjacencyBoost } from './training/scoring/contextualAdjacencyBoost';
// Batch 18-D20: the private calculateFitmentScore helper + FITMENT_WEIGHTS
// constants were moved into a shared contextual scoring module and the
// helper renamed `calculateContextualFitmentScore` for cross-scorer
// namespace clarity. The helper body is text-identical; the call site
// preserves its destructure shape, and the downstream `vehicle_specific`
// source flip + explanation prefix logic at the call site is unchanged.
// `matchType` remains load-bearing (see header docstring of the new module).
import { calculateContextualFitmentScore } from './training/scoring/contextualFitmentScore';

// =============================================
// TYPES
// =============================================

export interface TicketContext {
  vehicleFuelType?: string | null;
  vehicleYear?: number | null;      // For fitment matching
  vehicleMake?: string | null;      // For fitment matching
  vehicleModel?: string | null;     // For fitment matching
  vehicleEngine?: string | null;    // For fitment matching
  jobTitles: string[];
  jobIds?: string[];                // For ID-based adjacency matching
  workflowText: string;
  workflowSources?: string[];
  dtcCodes?: string[];
  mode: SidebarMode;
}

export interface ScoredContextualArtifact {
  artifact: TrainingArtifact;
  score: number;
  matchedKeywords: string[];
  matchedDTCs: string[];
  explanation: string;
  confidenceBadge: 'verified' | 'community' | 'provisional';
  source: 'job_specific' | 'domain' | 'keyword_match' | 'general' | 'vehicle_specific';
  isLongerFallback?: boolean; // For Job Specific mode when no short content exists
  fitmentMatchType?: string | null; // 'exact_ymm' | 'make_model' | 'make_only' | null
}

export interface FitmentInfo {
  artifactId: string;
  year: number | null;
  make: string | null;
  model: string | null;
  engine: string | null;
  boost: number;
}

export interface AdjacencyInfo {
  sourceJobId: string;
  relatedJobId: string;
  boost: number;
}

interface JobMappingInfo {
  artifactId: string;
  relevanceWeight: number;
}

// =============================================
// CONSTANTS
// =============================================

const SCORE_WEIGHTS = {
  KEYWORD_TITLE_MATCH: 10,
  KEYWORD_NOTES_MATCH: 5,
  DTC_MATCH: 15,
  JOB_MAPPING_BONUS: 20,
};

// Batch 18-D6: CONFIDENCE_MULTIPLIERS moved to the shared scoring module.
// Imported at the top of this file; the call site below is unchanged.

// Batch 18-D20: FITMENT_WEIGHTS moved with `calculateContextualFitmentScore`
// into `./training/scoring/contextualFitmentScore.ts`. Values are
// unchanged.

/**
 * Duration-based scoring rules by mode
 */
const DURATION_RULES = {
  job_specific: {
    hardMaxMinutes: 30,  // Hard filter above this
    boosts: [
      { maxMinutes: 15, boost: 20 },  // Very short = strong boost
      { maxMinutes: 30, boost: 10 },  // Under threshold = boost
    ],
  },
  foundational: {
    boosts: [
      { minMinutes: 45, boost: 15 },   // Long-form = boost
      { minMinutes: 30, boost: 5 },    // Medium = slight boost
    ],
    penalties: [
      { maxMinutes: 15, penalty: -10 }, // Ultra-short = penalize
    ],
    liveSessionBoost: 20,  // Boost live/in-person
  },
};

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Extract DTCs from text (supports P, B, C, U codes)
 */
export function extractDTCs(text: string): string[] {
  if (!text) return [];
  const matches = text.match(/[PpBbCcUu][0-9]{4}/g) || [];
  return [...new Set(matches.map(m => m.toUpperCase()))];
}

/**
 * Normalize text for keyword matching
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')  // Replace punctuation with spaces
    .replace(/\s+/g, ' ')        // Collapse multiple spaces
    .trim();
}

/**
 * Check if artifact applies to vehicle fuel type
 * HARD FILTER - fuel gate must always win
 */
function passesFuelTypeGate(
  artifact: TrainingArtifact,
  vehicleFuelType: string | null | undefined
): boolean {
  const fuelTypes = (artifact as any).vehicle_fuel_types as string[] | null | undefined;
  
  // NULL/empty = applies to all (safe default)
  if (!fuelTypes || fuelTypes.length === 0) return true;
  
  // Explicit "any" = applies to all
  if (fuelTypes.includes('any')) return true;
  
  // No vehicle fuel type known = don't filter (non-blocking)
  if (!vehicleFuelType) return true;
  
  // Must match
  return fuelTypes.includes(vehicleFuelType.toLowerCase());
}

/**
 * Find keyword matches in text. Supports multi-word phrases like "rough idle".
 *
 * FIX-R47-HIGH-1 + MED-1:
 *  - Single-word keywords use a word-boundary regex (`\b{word}\b`) to
 *    prevent substring false-positives. Without this, an artifact tagged
 *    `brake` would falsely match a ticket containing `braking`, `fuel`
 *    would match `powerfuel`, `abs` would match `absorber`, etc. — all
 *    real false-positive paths that would surface irrelevant training.
 *  - Multi-word phrases (containing a space) keep substring matching
 *    because internal-word collision isn't a concern when the phrase
 *    itself anchors the meaning ("rough idle" can only collide as a
 *    full phrase, not as a fragment of a longer compound).
 *  - Defensive: skip null / non-string keyword entries (Supabase
 *    jsonb arrays don't enforce element type at the schema level).
 *  - The returned `matches` array uses the ORIGINAL keyword strings so
 *    explanation rendering preserves author capitalization.
 */
function findKeywordMatches(
  keywords: string[],
  text: string
): string[] {
  if (!keywords || !text) return [];

  const normalizedText = normalizeText(text);
  const matches: string[] = [];

  for (const rawKeyword of keywords) {
    if (!rawKeyword || typeof rawKeyword !== 'string') continue;
    const normalizedKeyword = normalizeText(rawKeyword);
    if (!normalizedKeyword) continue;

    const isMultiWord = normalizedKeyword.includes(' ');
    let isMatch = false;
    if (isMultiWord) {
      // Multi-word phrase: substring match is the right semantic.
      isMatch = normalizedText.includes(normalizedKeyword);
    } else {
      // Single word: enforce word boundary to stop substring leaks.
      const escaped = normalizedKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp(`\\b${escaped}\\b`, 'i');
      isMatch = wordBoundaryRegex.test(normalizedText);
    }
    if (isMatch) matches.push(rawKeyword);
  }

  return [...new Set(matches)];
}

/**
 * Find DTC matches between artifact DTCs and extracted DTCs
 */
function findDTCMatches(
  artifactDTCs: string[] | null | undefined,
  extractedDTCs: string[]
): string[] {
  if (!artifactDTCs || !extractedDTCs.length) return [];
  
  return artifactDTCs.filter(code => 
    extractedDTCs.includes(code.toUpperCase())
  );
}

/**
 * Calculate duration score based on mode
 */
function calculateDurationScore(
  artifact: TrainingArtifact,
  mode: SidebarMode
): { score: number; boosted: boolean } {
  const minutes = artifact.estimated_time_minutes;
  if (!minutes) return { score: 0, boosted: false };
  
  if (mode === 'job_specific') {
    const rules = DURATION_RULES.job_specific;
    for (const boost of rules.boosts) {
      if (minutes <= boost.maxMinutes) {
        return { score: boost.boost, boosted: true };
      }
    }
    return { score: 0, boosted: false };
  } else {
    // Foundational mode
    const rules = DURATION_RULES.foundational;
    let score = 0;
    let boosted = false;
    
    // Check boosts
    for (const boost of rules.boosts) {
      if (minutes >= boost.minMinutes) {
        score += boost.boost;
        boosted = true;
        break; // Take first matching boost
      }
    }
    
    // Check penalties
    for (const penalty of rules.penalties) {
      if (minutes <= penalty.maxMinutes) {
        score += penalty.penalty;
      }
    }
    
    // Boost live/in-person
    if (artifact.delivery_format === 'live' || 
        artifact.artifact_type === 'live_seminar' || 
        artifact.artifact_type === 'hands_on_lab') {
      score += rules.liveSessionBoost;
      boosted = true;
    }
    
    return { score, boosted };
  }
}

// Batch 18-D26: removed dead export `passesDurationFilter` after the
// 18-D25 audit confirmed zero consumers across production, tests, docs,
// and dynamic access. This does not change runtime behavior. The
// DURATION_RULES.job_specific.hardMaxMinutes = 30 rule was documented
// as a hard filter but the helper that enforced it was never called;
// whether to enforce it is a separate product decision and is
// intentionally NOT addressed in this batch.

// Batch 18-D12: generateExplanation moved to
// `./training/explanations/contextualExplanation.ts` and is now imported
// at the top of this file as `generateContextualExplanation`. The call
// site in scoreArtifactsForTicket uses the new identifier; behavior
// is unchanged.

// Batch 18-D14: getConfidenceBadge moved to
// `./training/scoring/confidenceBadge.ts` and is now imported at the
// top of this file. The call site in scoreArtifactsForTicket is
// unchanged.

// =============================================
// FITMENT & ADJACENCY SCORING FUNCTIONS
// =============================================

// Batch 18-D20: calculateFitmentScore moved to
// `./training/scoring/contextualFitmentScore.ts` and renamed
// `calculateContextualFitmentScore`. Imported at the top of this file;
// call site in scoreArtifactsForTicket uses the new identifier. The
// downstream `vehicle_specific` source flip + explanation prefix logic
// stays at the call site and is unchanged.

// Batch 18-D17: calculateAdjacencyBoost moved to
// `./training/scoring/contextualAdjacencyBoost.ts` and renamed
// `calculateContextualAdjacencyBoost`. Imported at the top of this
// file; call site in scoreArtifactsForTicket uses the new identifier.

// =============================================
// MAIN SCORING FUNCTION
// =============================================

/**
 * Score and rank training artifacts based on ticket context
 * 
 * @param artifacts - Training artifacts to score
 * @param context - Ticket context (fuel type, job titles, notes, mode, vehicle info)
 * @param jobMappings - Optional job-specific mappings for boost
 * @param fitmentData - Optional vehicle fitment data for boost
 * @param artifactJobIds - Optional map of artifact ID to array of job IDs (for adjacency)
 * @param adjacencyMap - Optional job adjacency data
 * @returns Scored artifacts sorted by relevance
 */
export function scoreArtifactsForTicket(
  artifacts: TrainingArtifact[],
  context: TicketContext,
  jobMappings: JobMappingInfo[] = [],
  fitmentData: FitmentInfo[] = [],
  artifactJobIds: Map<string, string[]> = new Map(),
  adjacencyMap: AdjacencyInfo[] = []
): ScoredContextualArtifact[] {
  const {
    vehicleFuelType,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    vehicleEngine,
    jobTitles,
    jobIds,
    workflowText,
    workflowSources,
    dtcCodes,
    mode,
  } = context;
  
  // Combine all text for keyword matching
  const combinedTitles = jobTitles.join(' ');
  const combinedText = `${combinedTitles} ${workflowText}`;
  
  // Extract DTCs from workflow text if not provided
  const extractedDTCs = dtcCodes || extractDTCs(workflowText);
  
  // Build job mapping lookup
  const mappingLookup = new Map<string, number>();
  for (const mapping of jobMappings) {
    mappingLookup.set(mapping.artifactId, mapping.relevanceWeight);
  }
  
  const scoredArtifacts: ScoredContextualArtifact[] = [];
  
  for (const artifact of artifacts) {
    // 1. FUEL TYPE GATE (hard filter)
    if (!passesFuelTypeGate(artifact, vehicleFuelType)) {
      continue; // Skip this artifact entirely
    }
    
    let score = 0;
    const artifactKeywords = (artifact as any).keywords as string[] | null;
    const artifactDTCs = (artifact as any).dtc_codes as string[] | null;
    
    // 2. KEYWORD MATCHING
    // Title matches (higher weight)
    const titleMatches = findKeywordMatches(artifactKeywords || [], combinedTitles);
    score += titleMatches.length * SCORE_WEIGHTS.KEYWORD_TITLE_MATCH;
    
    // Notes matches (medium weight)
    const notesMatches = findKeywordMatches(artifactKeywords || [], workflowText);
    // Only count notes matches that aren't already in title matches
    const uniqueNotesMatches = notesMatches.filter(m => !titleMatches.includes(m));
    score += uniqueNotesMatches.length * SCORE_WEIGHTS.KEYWORD_NOTES_MATCH;
    
    // All matched keywords (deduplicated)
    const matchedKeywords = [...new Set([...titleMatches, ...notesMatches])];
    
    // 3. DTC MATCHING (high weight)
    const matchedDTCs = findDTCMatches(artifactDTCs, extractedDTCs);
    score += matchedDTCs.length * SCORE_WEIGHTS.DTC_MATCH;
    
    // Boost DTC matches more if mode is job_specific
    if (mode === 'job_specific' && matchedDTCs.length > 0) {
      score += 5; // Extra bonus for immediate help context
    }
    
    // 4. JOB MAPPING BOOST
    const hasJobMapping = mappingLookup.has(artifact.id);
    let source: ScoredContextualArtifact['source'] = 'general';
    
    if (hasJobMapping) {
      const relevanceWeight = mappingLookup.get(artifact.id) || 1.0;
      score += SCORE_WEIGHTS.JOB_MAPPING_BONUS * relevanceWeight;
      source = 'job_specific';
    } else if (matchedKeywords.length > 0 || matchedDTCs.length > 0) {
      source = 'keyword_match';
    }
    
    // 5. CONFIDENCE BOOST (multiplier)
    const confidenceMultiplier = CONFIDENCE_MULTIPLIERS[artifact.confidence_level || 'provisional'] || 1.0;
    score *= confidenceMultiplier;
    
    // 6. DURATION SCORING (mode-based)
    // 18-D13: dropped the now-dead `boosted` destructuring. It was only
    // ever flowed into generateContextualExplanation's vestigial
    // `durationBoosted` parameter, which has been removed. The score
    // field is the only consumed return value here.
    const { score: durationScore } = calculateDurationScore(artifact, mode);
    score += durationScore;
    
    // 7. FITMENT SCORING (vehicle-specific boost)
    const artifactFitments = fitmentData.filter(f => f.artifactId === artifact.id);
    const { score: fitmentScore, matchType: fitmentMatchType } = calculateContextualFitmentScore(artifactFitments, context);
    
    // Only apply fitment boost if fuel gate compatible (already checked)
    if (fitmentScore > 0) {
      score += fitmentScore;
      // Mark source as vehicle-specific if exact Y/M/M match
      if (fitmentMatchType === 'exact_ymm') {
        source = 'vehicle_specific';
      }
    }
    
    // 8. ADJACENCY SCORING (related jobs boost, ID-based)
    const jobIdsForArtifact = artifactJobIds.get(artifact.id) || [];
    const ticketJobIds = jobIds || [];
    const adjacencyBoost = calculateContextualAdjacencyBoost(
      jobIdsForArtifact,
      ticketJobIds,
      adjacencyMap
    );
    if (adjacencyBoost > 0) {
      score += adjacencyBoost;
    }
    
    // Generate explanation (enhanced with fitment info)
    let explanation = generateContextualExplanation(
      matchedKeywords,
      matchedDTCs,
      hasJobMapping,
      source,
      artifact,
    );
    
    // Add fitment info to explanation if applicable
    if (fitmentMatchType === 'exact_ymm' && vehicleYear && vehicleMake && vehicleModel) {
      explanation = `${vehicleYear} ${vehicleMake} ${vehicleModel} specific • ${explanation}`;
    } else if (fitmentMatchType === 'make_model' && vehicleMake && vehicleModel) {
      explanation = `${vehicleMake} ${vehicleModel} relevant • ${explanation}`;
    }
    
    scoredArtifacts.push({
      artifact,
      score,
      matchedKeywords,
      matchedDTCs,
      explanation,
      confidenceBadge: getConfidenceBadge(artifact.confidence_level),
      source,
      fitmentMatchType,
    });
  }
  
  // Sort by score descending
  return scoredArtifacts.sort((a, b) => b.score - a.score);
}

// Batch 18-D26: removed dead export `getTopRecommendations` after the
// 18-D25 audit confirmed zero consumers. The actual production consumer
// (`useScheduleAwareRecommendations.ts`) hand-rolls the same
// `filter(score >= minScore).slice(0, maxResults)` pattern inline, so
// this utility was orphaned. No runtime behavior changes.
